import { type IMapWidget } from '../IMapWidget.ts';
import { MapWidgetLogic } from '../MapWidgetLogic.ts';
import { type IMapLayer, type IMapLayerIntersection } from './MapLayer.ts';

/**
 * Specializzazione di un `IMapLayer` con intersezione abilitata,
 * con indice e codice.
 */
export interface IMapIntersectionLayer extends IMapLayer {
  intersection: IMapLayerIntersection;
}

/**
 * Rappresenta un layer con intersezione abilitata, con indice e codice.
 */
export interface IntersectionLayerInfo {
  layer: IMapIntersectionLayer;
  index: number;
  code: string;
}

/**
 * Filtra i layer con intersezione abilitata e ritorna indice, code e config
 */
export function getIntersectionLayersInfo(widget: IMapWidget) {
  return (widget.layers ?? [])
    .map((layer, index) => ({
      layer,
      index,
      code: layer.code,
    }))
    .filter(
      (entry): entry is IntersectionLayerInfo =>
        !!entry.layer.intersection?.enabled,
    );
}

/**
 * Indica se il profilo di build corrente abilita le intersezioni del tipo
 * indicato, secondo due assi indipendenti:
 * - preset catastale → funzionalità catastali (ENABLE_CADASTRAL_PARTICLES),
 *   coerente con ricerca catastale e preset;
 * - intersezione custom → funzionalità avanzate (ENABLE_ADVANCED_MAP_WIDGET).
 *
 * Non considera se l'intersezione è già abilitata sul layer: serve sia al
 * configuratore (che deve mostrare la sezione per abilitarla) sia al render
 * runtime. Per quest'ultimo, che richiede anche `intersection.enabled`, usare
 * `isRenderableIntersectionLayer`.
 */
export function isIntersectionAllowedByFlags(
  intersection: Pick<IMapLayerIntersection, 'cadastralPreset'>,
): boolean {
  return intersection.cadastralPreset === true
    ? MapWidgetLogic.isCadastralEnabled()
    : MapWidgetLogic.isAdvancedMapWidgetEnabled();
}

/**
 * Indica se l'intersezione del layer va mostrata all'utente (lista di
 * configurazione e tabella a runtime): l'intersezione dev'essere abilitata sul
 * layer e il tipo dev'essere consentito dal profilo di build
 * (`isIntersectionAllowedByFlags`).
 *
 * Riguarda la sola visualizzazione: il calcolo runtime usa sempre
 * `getIntersectionLayersInfo` (non filtrato), per non far divergere i dati
 * salvati dalla geometria anche in profilo basic.
 */
export function isRenderableIntersectionLayer(
  layer: IMapLayer,
): layer is IMapIntersectionLayer {
  if (!layer.intersection?.enabled) return false;
  return isIntersectionAllowedByFlags(layer.intersection);
}

/**
 * Variante su lista di layer di `isRenderableIntersectionLayer`, per i
 * consumer che lavorano direttamente sui layer (es. il configuratore).
 */
export function getRenderableIntersectionLayers(
  layers: IMapLayer[],
): IMapIntersectionLayer[] {
  return layers.filter(isRenderableIntersectionLayer);
}
